import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

export function useAddToHomescreenPrompt() {
  const [prompt, setPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isIPad, setIsIPad] = useState(false);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const isRunningStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                               window.navigator.standalone ||
                               document.referrer.includes('android-app://');
    
    setIsStandalone(isRunningStandalone);

    const userAgent = window.navigator.userAgent.toLowerCase();
    const ios = /iphone|ipod/.test(userAgent);
    const ipad = /ipad/.test(userAgent) || 
                (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    
    setIsIOS(ios);
    setIsIPad(ipad);
  }, []);

  useEffect(() => {
    const checkInstallability = async () => {
      if (isStandalone) {
        setIsInstallable(false);
        return;
      }

      if (window.matchMedia('(display-mode: standalone)').matches) {
        setIsInstallable(false);
        return;
      }

      if ('getInstalledRelatedApps' in navigator) {
        const installedApps = await navigator.getInstalledRelatedApps();
        if (installedApps.length > 0) {
          setIsInstallable(false);
          return;
        }
      }

      setIsInstallable(true);
    };

    checkInstallability();

    const ready = (e) => {
      e.preventDefault();
      window.deferredPrompt = e;
      setPrompt(e);
      setIsInstallable(true);
    };

    const handleAppInstalled = () => {
      setIsInstallable(false);
      setPrompt(null);
      window.deferredPrompt = null;
    };

    const timeoutId = setTimeout(() => {
      window.addEventListener("beforeinstallprompt", ready);
      window.addEventListener('appinstalled', handleAppInstalled);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("beforeinstallprompt", ready);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [setPrompt, setIsInstallable, location.pathname, isStandalone]);

  const promptToInstall = async () => {
    if (isIOS || isIPad) {
      const device = isIPad ? 'iPad' : 'iPhone';
      alert(
        `To install this app on your ${device}:\n\n` +
        '1. Tap the Share button (square with arrow) in the browser toolbar.\n' +
        '2. Scroll down and tap "Add to Home Screen".\n' +
        '3. Enter a name for the app and tap "Add".\n\n' +
        'Note: If you don\'t see the Share button, try scrolling to the top of the page first.'
      );
      return;
    }
    
    if (prompt) {
      try {
        const result = await prompt.prompt();
        window.deferredPrompt = null;
        setPrompt(null);
        setIsInstallable(false);
        return result;
      } catch (error) {
        console.error('Error installing PWA:', error);
      }
    }
  };

  return [promptToInstall, isIOS || isIPad, isInstallable && !isStandalone];
} 